import type { QueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import type {
  TApplicationStatus,
  TBulkSimulateApplicationInputDTO,
  TCreateApplicationInputDTO,
  TCreateApplicationWithPersonInputDTO,
  TCreateGuarantorInputDTO,
} from '@doutorsim/shared/applications'
import type { TCreateCompanyInputDTO, TUpdateCompanyInputDTO } from '@doutorsim/shared/companies'
import type { TAttachmentType } from '@doutorsim/shared/exposed/application-attachments'
import type { TConfirmedDeclarationsDataDTO } from '@doutorsim/shared/exposed/person-declarations'
import type { TCreateFacilityInputDTO, TUpdateFacilityInputDTO } from '@doutorsim/shared/facilities'
import type { TCreatePersonInputDTO, TUpdatePersonActionInputDTO } from '@doutorsim/shared/persons'
import type { TUpdateUserInputDTO } from '@doutorsim/shared/users'

import { api } from '../api'
import { unwrapApiResult } from './helpers'
import { queries } from './queries'

export const mutations = {
  attachments: {
    upload: (queryClient: QueryClient) => ({
      mutationFn: async ({
        applicationId,
        file,
        type,
      }: {
        applicationId: string
        file: File
        type: TAttachmentType
      }) => {
        const { id: attachmentId, uploadUrl } = unwrapApiResult(
          await api.attachments.create(applicationId, {
            filename: file.name,
            contentType: file.type,
            type,
            sizeBytes: file.size,
          }),
        )

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        })

        if (!uploadResponse.ok) {
          throw new Error('Falha ao enviar o arquivo. Tente novamente.')
        }

        return unwrapApiResult(await api.attachments.confirmUpload(applicationId, attachmentId))
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.attachments._def })
      },
    }),
    delete: (queryClient: QueryClient) => ({
      mutationFn: async ({
        applicationId,
        attachmentId,
      }: {
        applicationId: string
        attachmentId: string
      }) => api.attachments.delete(applicationId, attachmentId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.attachments._def })
      },
    }),
  },

  applications: {
    create: (queryClient: QueryClient) => ({
      mutationFn: async (data: TCreateApplicationInputDTO) =>
        unwrapApiResult(await api.applications.createApplication(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.applications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.companyApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplicationCards._def })
      },
    }),
    update: (queryClient: QueryClient) => ({
      mutationFn: async ({ id, data }: { id: string; data: Partial<TCreateApplicationInputDTO> }) =>
        unwrapApiResult(await api.applications.updateApplication(id, data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.applications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.companyApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplicationCards._def })
      },
    }),
    delete: (queryClient: QueryClient) => ({
      mutationFn: (id: string) => api.applications.deleteApplication(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.applications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.companyApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplicationCards._def })
      },
    }),
    createGuarantor: (queryClient: QueryClient) => ({
      mutationFn: async ({
        applicationId,
        data,
      }: {
        applicationId: string
        data: TCreateGuarantorInputDTO
      }) => unwrapApiResult(await api.applications.createGuarantor(applicationId, data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.applications.detail._def })
        queryClient.invalidateQueries({ queryKey: queries.applications.detailFull._def })
      },
    }),
    createWithPersons: (queryClient: QueryClient) => ({
      mutationFn: async (data: TCreateApplicationWithPersonInputDTO) =>
        unwrapApiResult(await api.applications.createApplicationWithPersons(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.applications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.companyApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplicationCards._def })
        queryClient.invalidateQueries({ queryKey: queries.persons._def })
      },
    }),
    releaseHandoff: (queryClient: QueryClient) => ({
      mutationFn: async ({
        applicationId,
        companyId,
        facilityId,
      }: {
        applicationId: string
        companyId: string
        facilityId: string
      }) =>
        unwrapApiResult(
          await api.applications.releaseHandoff(applicationId, companyId, facilityId),
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.applications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.companyApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplicationCards._def })
      },
    }),
    bulkSimulate: () => ({
      mutationFn: async (simulationData: TBulkSimulateApplicationInputDTO) => {
        return unwrapApiResult(
          await api.applications.bulkSimulateApplication({
            disbursementDate: dayjs(simulationData.disbursementDate).format('YYYY-MM-DD'),
            firstPaymentDate: dayjs(simulationData.firstPaymentDate).format('YYYY-MM-DD'),
            requestedAmount: simulationData.requestedAmount,
            mode: simulationData.mode,
          }),
        )
      },
    }),
  },

  persons: {
    create: (queryClient: QueryClient) => ({
      mutationFn: async (data: TCreatePersonInputDTO) =>
        unwrapApiResult(await api.persons.createPerson(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.persons._def })
      },
    }),
    update: (queryClient: QueryClient) => ({
      mutationFn: async (data: TUpdatePersonActionInputDTO) =>
        unwrapApiResult(await api.persons.updatePerson(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.persons._def })
        queryClient.invalidateQueries({ queryKey: queries.applications._def })
      },
    }),
  },

  companies: {
    update: (queryClient: QueryClient) => ({
      mutationFn: async (data: TUpdateCompanyInputDTO) =>
        unwrapApiResult(await api.companies.update(data)),
      onSuccess: (_data: unknown, variables: TUpdateCompanyInputDTO) => {
        queryClient.invalidateQueries({
          queryKey: queries.companies.detail(variables.id).queryKey,
        })
        queryClient.invalidateQueries({ queryKey: queries.staffCompanies._def })
      },
    }),
    updateFacility: (queryClient: QueryClient) => ({
      mutationFn: async (data: TUpdateFacilityInputDTO) =>
        unwrapApiResult(
          await api.companies.facilities.update(data.companyId, data.id, {
            name: data.name,
            legalName: data.legalName,
            cnpj: data.cnpj,
            address: data.address,
            contactInfo: data.contactInfo,
            bankAccount: data.bankAccount,
          }),
        ),
      onSuccess: (_data: unknown, variables: TUpdateFacilityInputDTO) => {
        queryClient.invalidateQueries({
          queryKey: queries.companies.facility(variables.companyId, variables.id).queryKey,
        })
        queryClient.invalidateQueries({ queryKey: queries.companies.facilities._def })
        queryClient.invalidateQueries({ queryKey: queries.staffFacilities._def })
      },
    }),
  },

  users: {
    update: (queryClient: QueryClient) => ({
      mutationFn: async (data: TUpdateUserInputDTO) =>
        unwrapApiResult(await api.users.update(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.users._def })
      },
    }),
    delete: (queryClient: QueryClient) => ({
      mutationFn: async (id: string) => unwrapApiResult(await api.users.delete(id)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.users._def })
      },
    }),
    invite: (queryClient: QueryClient) => ({
      mutationFn: async (data: { email: string; companyId: string; role: string }) =>
        unwrapApiResult(await api.staff.invitations.invite(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.users._def })
      },
    }),
  },

  staffApplications: {
    updateStatus: (queryClient: QueryClient) => ({
      mutationFn: ({
        applicationId,
        status,
      }: {
        applicationId: string
        status: TApplicationStatus
      }) => api.staff.applications.updateApplicationStatus(applicationId, status),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.staffApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplicationCards._def })
      },
    }),
    updateDeclarationsStatus: (queryClient: QueryClient) => ({
      mutationFn: ({
        applicationId,
        status,
        reason,
        confirmedData,
      }: {
        applicationId: string
        status: 'confirmed' | 'rejected'
        reason?: string
        confirmedData?: TConfirmedDeclarationsDataDTO
      }) =>
        api.staff.applications.updatePersonDeclarationsStatus(applicationId, {
          status,
          reason,
          confirmedData,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.staffApplications._def })
        queryClient.invalidateQueries({ queryKey: queries.staffApplicationCards._def })
        queryClient.invalidateQueries({ queryKey: queries.applications._def })
        queryClient.invalidateQueries({ queryKey: queries.persons._def })
      },
    }),
  },

  staffCompanies: {
    create: (queryClient: QueryClient) => ({
      mutationFn: async (data: TCreateCompanyInputDTO) =>
        unwrapApiResult(await api.staff.companies.create(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.staffCompanies._def })
      },
    }),
  },

  staffFacilities: {
    create: (queryClient: QueryClient) => ({
      mutationFn: (data: TCreateFacilityInputDTO) => api.staff.facilities.create(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queries.staffFacilities._def })
        queryClient.invalidateQueries({ queryKey: queries.companies._def })
      },
    }),
  },
}
